/* eslint-disable @typescript-eslint/no-deprecated */
import { format } from 'date-fns';
import * as accounting from 'accounting';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import LocalStorage from './localStorage';
import type { CodexOrder } from '../types/codexOrder';
import type { OrderlineOrder } from '../types/orderlineOrder';
import type { OrderStatusType } from '../constants/OrderStatus';

/**
 * Formats a number as currency using the specified symbol and precision.
 */
export function formatMoney(value: string | number | undefined, precision = 2) {
  if (!value) {
    return '';
  }
  const currency = LocalStorage.getCompanyCurrency() || '';
  return accounting.formatMoney(
    +value,
    currency,
    precision,
    ',',
    '.',
    !currency ? '%v' : '%s%v',
  );
}

/**
 * Capitalizes the first letter of a given string.
 */
export function capitalize(value: string) {
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

/**
 * Formats a timestamp into a readable date string.
 */
export function formatDate(ts: number, formatDate = 'dd MMM yyyy, hh:mm a') {
  const date = new Date(ts);
  return format(date, formatDate);
}

/**
 * Formats a raw phone number string into international format.
 */
export function formatPhoneNumber(raw: string) {
  const phoneNumber = parsePhoneNumberFromString(raw);
  if (!phoneNumber) return raw;
  return phoneNumber.formatInternational();
}

/**
 * Generates a platform-specific map URL to open a location in the maps app.
 */
export function getRedirectUrlForMaps(
  label: string,
  latitude: string,
  longitude: string,
) {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isAndroid) {
    return `geo:${latitude},${longitude}?q=${latitude},${longitude}(${encodeURIComponent(label)})`;
  }

  if (isIOS) {
    return `maps://maps.apple.com/?q=${encodeURIComponent(label)}&ll=${latitude},${longitude}`;
  }

  return `https://www.google.com/maps?q=${latitude},${longitude}`;
}

/**
 * Detect if device is IOS or not
 */
export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || navigator.vendor;
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return true;
  }

  const isMac = navigator.platform === 'MacIntel';
  const hasTouch = navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
  if (isMac && hasTouch) {
    return true;
  }

  return false;
}

/**
 * Handle opening of a URL
 */
export function openUrl(url: string) {
  if (url.startsWith('http')) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
}

/**
 * Update CSS variables for brand colors
 */
export function updateBrandColors(primary: string, secondary: string) {
  if (!primary || !secondary) {
    return;
  }

  let textColor;
  let iconColor;
  let highlightColor;
  let backgroundColor;

  if (secondary === '#ffffff') {
    iconColor = primary;
    textColor = secondary;
    highlightColor = primary;
    backgroundColor = primary;
  } else {
    textColor = secondary;
    iconColor = secondary;
    backgroundColor = primary;
    highlightColor = secondary;
  }

  document.documentElement.style.setProperty('--color-brand-text', textColor);
  document.documentElement.style.setProperty('--color-brand-icon', iconColor);
  document.documentElement.style.setProperty(
    '--color-brand-highlight',
    highlightColor,
  );
  document.documentElement.style.setProperty(
    '--color-brand-background',
    backgroundColor,
  );
}

/**
 * Detects if the current device is desktop/web.
 */
export function isWebDevice() {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || navigator.vendor;
  const isDesktopUA = /Windows NT|Macintosh|Linux x86_64|X11/i.test(ua);
  const isLargeScreen = window.innerWidth > 1024;

  return isDesktopUA || isLargeScreen;
}

/**
 * Transform orderline order to codex order structure
 */
export function transformToCodexPayload(olOrder: OrderlineOrder): CodexOrder {
  const fullOrder = olOrder?.data?.order ?? {};
  const {
    id,
    type,
    meta,
    biz_id,
    status,
    payload,
    location,
    created_by,
    reference_id,
    customer_phone,
    platform_order_id,
  } = fullOrder;
  const deliveryAddress = meta?.delivery_address;
  const { order: orderData, customer } = payload || {};

  return {
    reference_id,
    settings: olOrder?.data?.settings,
    order: {
      customer: {
        name: customer?.name,
        phone: `${olOrder?.data?.settings?.isd_code ?? ''}${customer_phone}`,
        email: customer?.email,
        address: {
          id: deliveryAddress?.id ?? orderData?.address_id,
          tag: deliveryAddress?.tag,
          pin: deliveryAddress?.pin,
          city: deliveryAddress?.city,
          latitude: deliveryAddress?.lat,
          longitude: deliveryAddress?.lng,
          line_1: deliveryAddress?.address_1,
          line_2: deliveryAddress?.address_2,
          landmark: deliveryAddress?.landmark,
          sub_locality: deliveryAddress?.sub_locality,
        },
      },
      order: {
        store: {
          id: location?.id,
          name: location?.name,
          phone: location?.phone,
          address: location?.address,
          latitude: location?.latitude,
          longitude: location?.longitude,
        },
        details: {
          id,
          biz_id: biz_id,
          created_by,
          order_source: meta?.order_source,
          order_module: meta?.order_module,
          order_type: type,
          pay_by: meta?.pay_by,
          state: status as OrderStatusType,
          order_total: orderData?.order_total,
          total_taxes: orderData?.order_taxes,
          channel: orderData?.details?.platform,
          order_state: status as OrderStatusType,
          payable_amount: orderData?.order_total,
          total_charges: orderData?.order_charges,
          order_subtotal: orderData?.order_subtotal,
          ext_platforms: [{ id: platform_order_id }],
          delivery_charge: orderData?.delivery_charge,
          instructions: orderData?.details?.instructions,
          charges: orderData?.charges,
          taxes: orderData?.taxes,
          tipping_amount: orderData?.tipping_amount,
          tipping_percentage: orderData?.tipping_percentage,
          brand: {
            id: location?.brand?.id,
            name: location?.brand?.name,
            logo_url: location?.brand?.logo_url,
          },
        },
        items:
          orderData?.items?.map(item => ({
            id: item.id,
            title: item.name,
            price: item.price,
            quantity: item.quantity,
            category_id: item.category_id,
            instructions: item.instructions,
            options_to_add:
              item?.options_to_add?.map(o => ({
                id: o.id,
                title: o.name,
                price: o.price,
                quantity: o.quantity,
              })) ?? [],
            options_to_remove:
              item?.options_to_remove?.map(o => ({
                id: o.id,
                title: o.name,
                price: o.price,
                quantity: o.quantity,
              })) ?? [],
          })) ?? [],
      },
    },
  };
}
